/**
 * Edit Transition Dialog
 * 
 * Reusable dialog component for editing transitions
 * Can be used in TransitionsProcessesView, Chat UI cards, and other components
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message, Spin, Tabs, Card, Switch } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { ArtifactStateTransitionType, DemandType, DemandFlowType } from '../../types/enums';
import type { PrimeArtifactStateTransition } from '../../types/primeArtifactStateTransition';
import { TransitionParametersEditor, ValidationRulesEditor } from '../Parameters';
import { RollupConfigurationPanel } from '../Parameters/RollupConfigurationPanel';
import { TransitionParameters, ValidationRules, ArtifactGenerationParameters } from '../../types/transitionParameters';
import { ARTIFACT_GENERATION_CATALOG } from '../../config/rollupParameterCatalog';
import { normalizeTransitionUpdate } from '../../utils/transitionNormalization';

const { TextArea } = Input;

interface EditTransitionDialogProps {
  visible: boolean;
  transitionId: string | null;
  artifactId: string;
  processId?: string; // Optional: for creating transitions within a specific process
  defaultRole?: string; // Optional: default transition type/role
  onClose: () => void;
  onSave?: () => void; // Optional callback after successful save
}

export const EditTransitionDialog: React.FC<EditTransitionDialogProps> = ({
  visible,
  transitionId,
  artifactId,
  processId,
  defaultRole,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState<any[]>([]);
  const [transition, setTransition] = useState<PrimeArtifactStateTransition | null>(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [transitionParams, setTransitionParams] = useState<TransitionParameters>({});
  const [validationRules, setValidationRules] = useState<ValidationRules>({});
  const [artifactName, setArtifactName] = useState<string>('');
  const [processName, setProcessName] = useState<string>('');
  const [processes, setProcesses] = useState<any[]>([]);

  // Load states and transition data when dialog opens
  useEffect(() => {
    if (visible) {
      if (transitionId) {
        void loadData();
      } else {
        // Create mode - just load states
        void loadStatesForCreate();
      }
    } else {
      // Reset state when dialog closes
      // Don't reset form here - it causes warning when form isn't mounted
      // Form will be reset when dialog opens next time
      setStates([]);
      setTransition(null);
      setActiveTab('basic'); // Reset active tab
      setTransitionParams({}); // Reset parameters
      setValidationRules({}); // Reset validation rules
      setArtifactName(''); // Reset artifact name
      setProcessName(''); // Reset process name
    }
  }, [visible, transitionId, artifactId, defaultRole, processId]); // Removed form from dependencies

  const generateTransitionName = (transitionType: string, fromStateName?: string, toStateName?: string) => {
    if (!artifactName) return '';
    
    const processPrefix = processName ? `${processName} ` : '';
    
    switch (transitionType) {
      case 'PROCESSITEM':
      case 'PROCESSITEM_PERSISTENT':
        // For process items, include state transition
        if (fromStateName && toStateName) {
          return `${processPrefix}${artifactName} ${fromStateName} → ${toStateName}`;
        } else if (fromStateName) {
          return `${processPrefix}${artifactName} from ${fromStateName}`;
        } else if (toStateName) {
          return `${processPrefix}${artifactName} to ${toStateName}`;
        } else {
          return `${processPrefix}${artifactName}`;
        }
      case 'MATERIAL':
        // For materials, include from/to states if available (consumed vs non-consumed)
        if (fromStateName && toStateName) {
          return `${processPrefix}${artifactName} (Material) ${fromStateName} → ${toStateName}`;
        }
        return `${processPrefix}${artifactName} (Material)`;
      case 'RESOURCE':
        // For resources, include from/to states if available
        if (fromStateName && toStateName) {
          return `${processPrefix}${artifactName} (Resource) ${fromStateName} → ${toStateName}`;
        }
        return `${processPrefix}${artifactName} (Resource)`;
      case 'CREATION':
        // For creation, only to state matters (what's being created)
        if (toStateName) {
          return `${processPrefix}Create ${artifactName} (${toStateName})`;
        }
        return `${processPrefix}Create ${artifactName}`;
      default:
        return `${processPrefix}${artifactName}`;
    }
  };

  const loadStatesForCreate = async () => {
    try {
      setLoading(true);
      // Reset form when loading states (form is mounted at this point)
      form.resetFields();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[EditTransitionDialog] loadStatesForCreate called with artifactId:', artifactId, 'processId:', processId);
      }
      
      // Load artifact info
      const artifactResponse = await apiClient.get(`/prime-artifacts/${artifactId}`);
      const artifact = artifactResponse.data;
      setArtifactName(artifact.name);
      
      // Load process info if processId is provided
      if (processId) {
        const processResponse = await apiClient.get(`/prime-processes/${processId}`);
        const process = processResponse.data;
        setProcessName(process.name);
      }
      
      // Load states for this artifact
      const statesResponse = await apiClient.get(`/prime-artifacts/${artifactId}/states`);
      const loadedStates = statesResponse.data || [];
      setStates(loadedStates);
      
      // Check if artifact has states
      if (loadedStates.length === 0) {
        message.warning(`Artifact "${artifact.name}" has no states. Please create states for this artifact first.`);
        onClose();
        return;
      }
      
      // Set default form values for create mode (name will be auto-generated when states are selected)
      form.setFieldsValue({
        name: '', // Will be auto-generated when user selects from/to states
        default_transition_type: defaultRole || 'PROCESSITEM',
        demand_type: DemandType.SUPPLY,  // Default: Demand is set by supply flowing in
        demand_flow_type: DemandFlowType.PULL,  // Default: Pull demand as needed
        is_improvement: false,
        transition_parameters: '{}',
        validation_rules: '{}'
      });
      console.log('[EditTransitionDialog] Form values set successfully');
    } catch (error) {
      console.error('[EditTransitionDialog] Error loading states:', error);
      message.error('Failed to load states');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      // Reset form when loading data (form is mounted at this point)
      form.resetFields();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[EditTransitionDialog] loadData called with transitionId:', transitionId);
      }
      
      // Load states for this artifact
      const statesResponse = await apiClient.get(`/prime-artifacts/${artifactId}/states`);
      const loadedStates = statesResponse.data || [];
      setStates(loadedStates);
      
      // Load artifact info for naming
      const artifactResponse = await apiClient.get(`/prime-artifacts/${artifactId}`);
      const artifact = artifactResponse.data;
      setArtifactName(artifact.name);
      
      // Load available processes for this artifact
      const processesResponse = await apiClient.get(`/prime-artifacts/${artifactId}/processes/`);
      setProcesses(processesResponse.data || []);
      
      // Load transition data
      const transitionResponse = await apiClient.get(`/prime-transitions/transition/${transitionId}`);
      const transitionData = transitionResponse.data;
      setTransition(transitionData);
      
      // Load process name if transition has a process reference
      let currentProcessName = '';
      if (transitionData.prime_process_ref) {
        try {
          const processResponse = await apiClient.get(`/prime-processes/${transitionData.prime_process_ref}`);
          currentProcessName = processResponse.data.name;
          setProcessName(currentProcessName);
        } catch (err) {
          console.error('[EditTransitionDialog] Error loading process:', err);
        }
      }
      
      // Auto-generate name instead of using stored name
      const fromStateName = transitionData.from_state?.name;
      const toStateName = transitionData.to_state?.name;
      const autoGeneratedName = generateTransitionName(
        transitionData.default_transition_type,
        fromStateName,
        toStateName
      );
      
      // Populate form with transition data (using auto-generated name or stored name as fallback)
      // Handle state references - use from_state_ref/to_state_ref if from_state/to_state objects aren't available
      // Convert to strings to ensure proper matching
      const fromStateId = transitionData.from_state?.id 
        ? String(transitionData.from_state.id)
        : (transitionData.from_state_ref ? String(transitionData.from_state_ref) : null);
      const toStateId = transitionData.to_state?.id
        ? String(transitionData.to_state.id)
        : (transitionData.to_state_ref ? String(transitionData.to_state_ref) : null);
      
      // Verify state IDs exist in loaded states
      const fromStateExists = fromStateId ? loadedStates.some((s: any) => String(s.id) === fromStateId) : true;
      const toStateExists = toStateId ? loadedStates.some((s: any) => String(s.id) === toStateId) : false;
      
      if (!toStateExists && toStateId) {
        console.warn('[EditTransitionDialog] To state ID not found in loaded states:', toStateId);
        console.warn('[EditTransitionDialog] Available state IDs:', loadedStates.map((s: any) => s.id));
      }
      if (!fromStateExists && fromStateId) {
        console.warn('[EditTransitionDialog] From state ID not found in loaded states:', fromStateId);
      }
      
      // Handle enum values - backend returns uppercase strings (e.g., "SUPPLY", "PULL")
      // The enum values are also uppercase, so we can use them directly
      const demandType = transitionData.demand_type 
        ? (typeof transitionData.demand_type === 'string' 
            ? transitionData.demand_type.toUpperCase() as DemandType
            : transitionData.demand_type)
        : DemandType.SUPPLY;
      const demandFlowType = transitionData.demand_flow_type
        ? (typeof transitionData.demand_flow_type === 'string'
            ? transitionData.demand_flow_type.toUpperCase() as DemandFlowType
            : transitionData.demand_flow_type)
        : DemandFlowType.PULL;
      
      const formValues = {
        name: autoGeneratedName || transitionData.name || 'Unnamed Transition',  // Fallback to stored name if auto-gen fails
        description: transitionData.description || '',
        from_state: fromStateId || null, // Use null for CREATION transitions
        to_state: toStateId || null,
        default_transition_type: transitionData.default_transition_type,
        demand_type: demandType,
        demand_flow_type: demandFlowType,
        prime_process_ref: transitionData.prime_process_ref || null,
        is_improvement: transitionData.is_improvement ?? false,
      };
      console.log('[EditTransitionDialog] Setting form values:', formValues);
      console.log('[EditTransitionDialog] Transition data received:', transitionData);
      console.log('[EditTransitionDialog] States available:', loadedStates.map((s: any) => ({ id: String(s.id), name: s.name })));
      console.log('[EditTransitionDialog] From state ID:', fromStateId, 'To state ID:', toStateId);
      
      // Set form values - ensure states are loaded first
      // Convert all state IDs in form values to strings to match Select option values
      const formValuesWithStringIds = {
        ...formValues,
        from_state: formValues.from_state ? String(formValues.from_state) : null,
        to_state: formValues.to_state ? String(formValues.to_state) : null,
        prime_process_ref: formValues.prime_process_ref ? String(formValues.prime_process_ref) : null,
      };
      
      form.setFieldsValue(formValuesWithStringIds);
      console.log('[EditTransitionDialog] Form values set successfully');
      
      // Force form to validate and update
      form.validateFields().catch(() => {
        // Ignore validation errors - fields might not be ready yet
      });
      
      // Set parameters and validation rules in state
      setTransitionParams(transitionData.transition_parameters || {});
      setValidationRules(transitionData.validation_rules || {});
      console.log('[EditTransitionDialog] Form values set successfully');
    } catch (error) {
      console.error('[EditTransitionDialog] Error loading transition data:', error);
      message.error('Failed to load transition data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Prepare payload using state values for parameters and validation
      // For CREATE: Backend expects different field names (prime_artifact_id, from_state_id, to_state_id, transition_type)
      // For UPDATE: Backend expects _ref suffixes (prime_artifact_ref, from_state_ref, to_state_ref, default_transition_type)
      // Also expects lowercase enum values for demand_type and demand_flow_type
      const basePayload: any = transitionId ? {
        // UPDATE payload - use _ref suffixes
        name: values.name,
        description: values.description,
        prime_artifact_ref: artifactId,
        from_state_ref: values.from_state,
        to_state_ref: values.to_state,
        default_transition_type: values.default_transition_type,
        demand_type: values.demand_type?.toLowerCase() || 'supply',
        demand_flow_type: values.demand_flow_type?.toLowerCase() || 'pull',
        state_parameters: transitionParams || {},
        validation_rules: validationRules || {},
        prime_process_ref: values.prime_process_ref === undefined ? null : values.prime_process_ref,
        is_improvement: values.is_improvement ?? false,
      } : {
        // CREATE payload - use _id suffixes and transition_type
        name: values.name,
        description: values.description,
        prime_artifact_id: artifactId,
        from_state_id: values.from_state,
        to_state_id: values.to_state,
        transition_type: values.default_transition_type,
        demand_type: values.demand_type?.toLowerCase() || 'supply',
        demand_flow_type: values.demand_flow_type?.toLowerCase() || 'pull',
        state_parameters: transitionParams || {},
        validation_rules: validationRules || {},
        prime_process_ref: processId || null,
        is_improvement: values.is_improvement ?? false,
      };

      // Use normalizeTransitionUpdate to ensure state_parameters and validation_rules are always objects
      const payload = normalizeTransitionUpdate(basePayload);

      if (transitionId) {
        console.log('[EditTransitionDialog] Edit mode - Process ref in payload:', payload.prime_process_ref);
      }

      console.log('[EditTransitionDialog] Saving payload:', JSON.stringify(payload, null, 2));

      if (transitionId) {
        // Update existing transition
        await apiClient.put(`/prime-artifacts/${artifactId}/transitions/${transitionId}`, payload);
        message.success('Transition updated successfully');
      } else {
        // Create new transition
        await apiClient.post(`/prime-artifacts/${artifactId}/transitions`, payload);
        message.success('Transition created successfully');
      }
      
      onSave?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving transition:', error);
      
      // Log validation errors if they exist
      if (error.errorFields) {
        console.error('[EditTransitionDialog] Validation errors:', error.errorFields);
        error.errorFields.forEach((err: any) => {
          console.error(`  - Field '${err.name.join('.')}': ${err.errors.join(', ')}`);
        });
      }
      
      let errorMessage = 'Failed to save transition';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle both string and object detail formats
        errorMessage = typeof detail === 'string' ? detail : detail.message || JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleTransitionTypeChange = (value: string) => {
    const currentValues = form.getFieldsValue();
    const fromStateName = states.find(s => s.id === currentValues.from_state)?.name;
    const toStateName = states.find(s => s.id === currentValues.to_state)?.name;
    
    const newName = generateTransitionName(value, fromStateName, toStateName);
    form.setFieldsValue({ name: newName });
  };

  const handleStateChange = () => {
    const currentValues = form.getFieldsValue();
    const transitionType = currentValues.default_transition_type;
    const fromStateName = states.find(s => s.id === currentValues.from_state)?.name;
    const toStateName = states.find(s => s.id === currentValues.to_state)?.name;
    
    // Auto-generate name for all transition types
    const newName = generateTransitionName(transitionType, fromStateName, toStateName);
    form.setFieldsValue({ name: newName });
  };

  const handleProcessChange = async (processId: string | null) => {
    // Handle "create new" option
    if (processId === '__create_new__') {
      await handleCreateNewProcess();
      return;
    }
    
    // Update process name state for auto-naming
    if (processId) {
      try {
        const processResponse = await apiClient.get(`/prime-processes/${processId}`);
        setProcessName(processResponse.data.name);
      } catch (error) {
        console.error('[EditTransitionDialog] Error loading process:', error);
        setProcessName('');
      }
    } else {
      setProcessName('');
    }
    
    // Trigger auto-naming with new process
    const currentValues = form.getFieldsValue();
    const transitionType = currentValues.default_transition_type;
    const fromStateName = states.find(s => s.id === currentValues.from_state)?.name;
    const toStateName = states.find(s => s.id === currentValues.to_state)?.name;
    
    const newName = generateTransitionName(transitionType, fromStateName, toStateName);
    form.setFieldsValue({ name: newName });
  };

  const handleCreateNewProcess = async () => {
    try {
      console.log('[EditTransitionDialog] Creating new process for transition:', transitionId);
      
      // Get the transition role from form values
      const currentValues = form.getFieldsValue();
      const transitionRole = currentValues.default_transition_type;
      
      console.log('[EditTransitionDialog] Using transition role:', transitionRole);
      
      // Step 1: If transition has a process, clear it first
      if (transition?.prime_process_ref) {
        console.log('[EditTransitionDialog] Clearing existing process before creating new one');
        
        // Save transition with null process
        const clearPayload: any = {
          name: currentValues.name,
          description: currentValues.description,
          prime_artifact_ref: artifactId,
          from_state_ref: currentValues.from_state,
          to_state_ref: currentValues.to_state,
          default_transition_type: currentValues.default_transition_type,
          demand_type: currentValues.demand_type,
          demand_flow_type: currentValues.demand_flow_type,
          transition_parameters: transitionParams,
          validation_rules: validationRules,
          prime_process_ref: null  // Clear the process
        };
        
        await apiClient.put(`/prime-artifacts/${artifactId}/transitions/${transitionId}`, clearPayload);
        console.log('[EditTransitionDialog] Process cleared successfully');
        
        // Update local state
        if (transition) {
          setTransition({ ...transition, prime_process_ref: null as any });
        }
        setProcessName('');
      }
      
      // Step 2: Generate the new process
      const response = await apiClient.post(`/prime-transitions/${transitionId}/generate-process`, {
        transition_role: transitionRole
      });
      
      console.log('[EditTransitionDialog] Process generation response:', response.data);
      message.success(response.data.message || 'Process generated successfully');
      
      // Step 3: Reload processes list
      const processesResponse = await apiClient.get(`/prime-artifacts/${artifactId}/processes/`);
      setProcesses(processesResponse.data || []);
      
      // Step 4: Update form with the newly created process
      const newProcess = response.data.process;
      if (newProcess && newProcess.id) {
        const newProcessId = newProcess.id;
        setProcessName(newProcess.name);
        
        // Update transition state
        if (transition) {
          setTransition({ ...transition, prime_process_ref: newProcessId });
        }
        
        // Set it in the form
        form.setFieldsValue({ prime_process_ref: newProcessId });
        
        // Trigger auto-naming with the new process
        const transitionType = currentValues.default_transition_type;
        const fromStateName = states.find(s => s.id === currentValues.from_state)?.name;
        const toStateName = states.find(s => s.id === currentValues.to_state)?.name;
        const newName = generateTransitionName(transitionType, fromStateName, toStateName);
        form.setFieldsValue({ name: newName });
      }
    } catch (error: any) {
      console.error('[EditTransitionDialog] Error creating process:', error);
      console.error('[EditTransitionDialog] Error response:', error.response?.data);
      const errorDetail = error.response?.data?.detail || error.message;
      message.error(`Failed to create process: ${errorDetail}`, 5);
      // Reset the select back to its previous value
      form.setFieldsValue({ prime_process_ref: transition?.prime_process_ref });
    }
  };

  const handleCancel = () => {
    // Only reset form if dialog is visible (form is mounted)
    if (visible) {
      try {
        form.resetFields();
      } catch (e) {
        // Form may not be connected, ignore
      }
    }
    onClose();
  };

  return (
    <Modal
      title={
        <div>
          <div>{transitionId ? "Edit Transition" : "Create Transition"}</div>
          {processName && (
            <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginTop: '4px' }}>
              Process: {processName}
            </div>
          )}
          {artifactName && (
            <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
              Artifact: {artifactName}
            </div>
          )}
        </div>
      }
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      confirmLoading={saving}
      width={900}
      okText="Save"
      cancelText="Cancel"
      destroyOnHidden={true}
    >
      <Spin spinning={loading}>
        <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                demand_type: DemandType.SUPPLY,
                demand_flow_type: DemandFlowType.PULL,
                is_improvement: false,
              }}
            >
          <Form.Item
            label="Transition Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a transition name' }]}
          >
            <Input placeholder="Enter transition name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={3} placeholder="Enter transition description" />
          </Form.Item>

          <Form.Item
            label="From State"
            name="from_state"
            rules={[
              {
                validator: async (_, value) => {
                  const transitionType = form.getFieldValue('default_transition_type');
                  // CREATION transitions don't need a "from" state
                  if (transitionType !== 'CREATION' && !value) {
                    return Promise.reject(new Error('Please select a from state'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select placeholder="Select starting state" onChange={handleStateChange}>
              {states.map(state => (
                <Select.Option key={state.id} value={state.id}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="To State"
            name="to_state"
            rules={[{ required: true, message: 'Please select a to state' }]}
          >
            <Select placeholder="Select destination state" onChange={handleStateChange}>
              {states.map(state => (
                <Select.Option key={state.id} value={state.id}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Transition Type"
            name="default_transition_type"
            rules={[{ required: true, message: 'Please select a transition type' }]}
          >
            <Select placeholder="Select transition type" onChange={handleTransitionTypeChange}>
              <Select.Option value={ArtifactStateTransitionType.OPERATIONAL}>
                Operational
              </Select.Option>
              <Select.Option value={ArtifactStateTransitionType.MATERIAL}>
                Material
              </Select.Option>
              <Select.Option value={ArtifactStateTransitionType.RESOURCE}>
                Resource
              </Select.Option>
              <Select.Option value={ArtifactStateTransitionType.CREATION}>
                Creation
              </Select.Option>
              <Select.Option value={ArtifactStateTransitionType.IMPROVEMENT}>
                Improvement
              </Select.Option>
            </Select>
          </Form.Item>

          {transitionId && (
            <Form.Item
              label="Process"
              name="prime_process_ref"
              tooltip="Associate this transition with a specific process (optional). Each artifact can only have one transition per process."
            >
              <Select 
                placeholder="Select process (optional)"
                allowClear
                onChange={handleProcessChange}
              >
                <Select.Option value="__create_new__" style={{ borderBottom: '1px solid #f0f0f0', fontWeight: 500, color: '#1890ff' }}>
                  <PlusOutlined style={{ marginRight: 8 }} />
                  {transition?.prime_process_ref ? 'Replace with New Process' : 'Create New Process'}
                </Select.Option>
                {processes.map(p => (
                  <Select.Option key={p.id} value={p.id}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

                    <Form.Item
                      label="Demand Type"
                      name="demand_type"
                      rules={[{ required: true, message: 'Please select a demand type' }]}
                      tooltip="How demand is set for this item"
                    >
            <Select placeholder="Select demand type">
              <Select.Option value={DemandType.TARGET}>
                <div>
                  <div>Target</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>A target value or rate is entered</div>
                </div>
              </Select.Option>
              <Select.Option value={DemandType.CAPACITY}>
                <div>
                  <div>Capacity</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Resource's capacity determines demand rate</div>
                </div>
              </Select.Option>
              <Select.Option value={DemandType.SUPPLY}>
                <div>
                  <div>Supply (default)</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Demand set by supply flowing in</div>
                </div>
              </Select.Option>
            </Select>
          </Form.Item>

                    <Form.Item
                      label="Demand Flow Type"
                      name="demand_flow_type"
                      rules={[{ required: true, message: 'Please select a demand flow type' }]}
                      tooltip="How demand flows through this transition"
                    >
            <Select placeholder="Select demand flow type">
              <Select.Option value={DemandFlowType.DRUM}>
                <div>
                  <div>Drum</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>This item's demand sets the demand flow</div>
                </div>
              </Select.Option>
              <Select.Option value={DemandFlowType.PULL}>
                <div>
                  <div>Pull (default)</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>This item will pull demand as needed</div>
                </div>
              </Select.Option>
              <Select.Option value={DemandFlowType.PUSH}>
                <div>
                  <div>Push</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Demand pushes to the next transition</div>
                </div>
              </Select.Option>
            </Select>
          </Form.Item>

            </Form>
              )
            },
            {
              key: 'parameters',
              label: 'Parameters',
              children: (
                <TransitionParametersEditor
                  parameters={transitionParams}
                  onChange={setTransitionParams}
                  mode="company"
                />
              )
            },
            {
              key: 'validation',
              label: 'Validation Rules',
              children: (
                <ValidationRulesEditor
                  validationRules={validationRules}
                  onChange={setValidationRules}
                />
              )
            },
            {
              key: 'rollup',
              label: 'Rollup Configuration',
              children: (
                <RollupConfigurationPanel
                  rollupParams={transitionParams.rollup_parameters || {}}
                  onChange={(rollupParams) => {
                    setTransitionParams({
                      ...transitionParams,
                      rollup_parameters: rollupParams,
                    });
                  }}
                  artifactType={artifactName}
                  disabled={saving}
                />
              )
            },
            {
              key: 'generation',
              label: 'Artifact Generation',
              children: (
                <div style={{ padding: '16px' }}>
              <Card 
                title="Artifact Generation Configuration" 
                size="small"
                extra={
                  <div style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                    For CREATION transitions only
                  </div>
                }
              >
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Generation Mode
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={transitionParams.artifact_generation_parameters?.artifact_generation_mode?.value}
                    onChange={(value) => {
                      setTransitionParams({
                        ...transitionParams,
                        artifact_generation_parameters: {
                          ...(transitionParams.artifact_generation_parameters || {}),
                          artifact_generation_mode: {
                            type: 'string' as const,
                            value: value,
                            description: 'Artifact generation mode'
                          }
                        }
                      });
                    }}
                    placeholder="Select generation mode..."
                    allowClear
                  >
                    {Object.values(ARTIFACT_GENERATION_CATALOG)
                      .filter(opt => opt.key === 'artifact_generation_mode')
                      .flatMap(opt => opt.options || [])
                      .map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          <div>
                            <div>{option.label}</div>
                            <div style={{ fontSize: '11px', color: '#888' }}>{option.description}</div>
                          </div>
                        </Select.Option>
                      ))
                    }
                  </Select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    Controls how many artifact items can be created concurrently
                  </div>
                </div>

                {/* Singleton Config */}
                {transitionParams.artifact_generation_parameters?.artifact_generation_mode?.value === 'singleton' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                      Replacement Behavior
                    </label>
                    <Select 
                      style={{ width: '100%' }}
                      value={transitionParams.artifact_generation_parameters?.replacement_behavior?.value}
                      onChange={(value) => {
                        setTransitionParams({
                          ...transitionParams,
                          artifact_generation_parameters: {
                            ...(transitionParams.artifact_generation_parameters || {}),
                            replacement_behavior: {
                              type: 'string' as const,
                              value: value,
                              description: 'Replacement behavior'
                            }
                          }
                        });
                      }}
                      placeholder="Select behavior..."
                    >
                      <Select.Option value="archive_previous">Archive Previous</Select.Option>
                      <Select.Option value="delete_previous">Delete Previous</Select.Option>
                      <Select.Option value="error_if_exists">Error If Exists</Select.Option>
                    </Select>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                      What to do if an artifact already exists
                    </div>
                  </div>
                )}

                {/* Sequential Config */}
                {transitionParams.artifact_generation_parameters?.artifact_generation_mode?.value === 'sequential' && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Completion Required
                      </label>
                      <Switch 
                        checked={transitionParams.artifact_generation_parameters?.completion_required?.value ?? true}
                        onChange={(checked) => {
                          setTransitionParams({
                            ...transitionParams,
                            artifact_generation_parameters: {
                              ...(transitionParams.artifact_generation_parameters || {}),
                              completion_required: {
                                type: 'boolean' as const,
                                value: checked,
                                description: 'Completion required'
                              }
                            }
                          });
                        }}
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Must previous item complete before creating next
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Max Active Items
                      </label>
                      <Input 
                        type="number"
                        value={transitionParams.artifact_generation_parameters?.max_active_items?.value ?? ''}
                        onChange={(e) => {
                          const numValue = parseInt(e.target.value) || undefined;
                          setTransitionParams({
                            ...transitionParams,
                            artifact_generation_parameters: {
                              ...(transitionParams.artifact_generation_parameters || {}),
                              max_active_items: numValue ? {
                                type: 'number' as const,
                                value: numValue,
                                description: 'Max active items'
                              } : undefined
                            }
                          });
                        }}
                        placeholder="e.g., 1 for strict sequential" 
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        Maximum simultaneously active items
                      </div>
                    </div>
                  </>
                )}

                {/* Concurrent Config */}
                {transitionParams.artifact_generation_parameters?.artifact_generation_mode?.value === 'concurrent' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                      Max Total Items (Optional)
                    </label>
                    <Input 
                      type="number"
                      value={transitionParams.artifact_generation_parameters?.max_total_items?.value ?? ''}
                      onChange={(e) => {
                        const numValue = parseInt(e.target.value) || undefined;
                        setTransitionParams({
                          ...transitionParams,
                          artifact_generation_parameters: {
                            ...(transitionParams.artifact_generation_parameters || {}),
                            max_total_items: numValue ? {
                              type: 'number' as const,
                              value: numValue,
                              description: 'Max total items'
                            } : undefined
                          }
                        });
                      }}
                      placeholder="Leave empty for unlimited" 
                    />
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                      Optional limit on total items
                    </div>
                  </div>
                )}
                </Card>
                </div>
              )
            }
          ]}
        />
      </Spin>
    </Modal>
  );
};

